import { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router";
import { DialogContent, Divider, Grid } from "@mui/material";
import axios from "axios";
import BASE_URL from "../../../constants/baseUrls";
import { LocaleContext } from "../../../routing/LangRouter";
import { useTranslation } from "react-i18next";
import { formatPersianDate } from "../../../utils/date";
import { numberWithCommas } from "../../../utils/utility";
import moment from "moment";

//import general components
import Loading from "../loading/loading";
import BaseDialog from "../base-dialog/base-dialog";
import EmptyOrErrorState from "../empty-or-error-state/empty-or-error-state";

//import images and icons
import { InfoCircle, Warning2 } from "iconsax-react";

//import styles
import "./without-modal.scss";

function WithoutDateModal({ open, setOpen, setRequestFlag }) {
    //general variables
    const navigate = useNavigate();
    const location = useLocation();
    const { locale } = useContext(LocaleContext);
    const { t } = useTranslation();
    const accessToken = localStorage.getItem("accessToken");

    //sessionStorage variables
    const domesticFlightInformation = JSON.parse(sessionStorage.getItem("domesticFlightInformation"));
    const intFlightInformation = JSON.parse(sessionStorage.getItem("intFlightInformation"));
    const hotelInformation = JSON.parse(sessionStorage.getItem("hotelInformation"));
    const itineraryInformation = JSON.parse(sessionStorage.getItem("itineraryInformation"));
    const { isTicket, isAccommodation } = JSON.parse(sessionStorage.getItem("generalInformation")) || {};
    const favorites = JSON.parse(sessionStorage.getItem("favorites"));

    const [information, setInformation] = useState();
    useEffect(() => {
        if (isTicket) {
            setInformation(domesticFlightInformation);
        }
        if (!isTicket && isAccommodation) {
            setInformation(domesticFlightInformation);
        }
        if (!isTicket && !isAccommodation) {
            setInformation(itineraryInformation);
        }
    }, []);

    const cities = `${locale === "en" ? information?.departure?.english_name : information?.departure?.name} ${t("to")} ${
        locale === "en" ? information?.destination?.english_name : information?.destination?.name
    }`;

    //handle close modal
    const handleClose = () => {
        setOpen(false);
        navigate(`/${locale}`, { state: { scrollToTravelForm: true } });
    };

    //without destination request
    const [loadingRecommender, setLoadingRecommender] = useState(false);
    const [dateRecommenderList, setDateRecommenderList] = useState();
    const [error, setError] = useState(false);
    const [isEmptyList, setIsEmptyList] = useState(false);
    const getRecommenderList = async () => {
        try {
            setLoadingRecommender(true);
            setError(false);
            setIsEmptyList(false);
            const data = {
                favourites: favorites,
                travelers: {
                    adults_count: information?.personCounter.adult,
                    children_count: information?.personCounter.child,
                    infants_count: information?.personCounter.infant,
                },
                user_id: accessToken ? JSON.parse(localStorage.getItem("userInformation")).id : 0,
                from_city_id: information.departure.id,
                to_city_id: information.destination.id,
            };

            const response = await axios.post(`${BASE_URL}/itinerary/time_recommender/`, data, {
                params: {
                    is_cake: false,
                },
            });
            setDateRecommenderList(response.data.data?.time_ranges);
            setIsEmptyList(!response.data.data?.time_ranges?.length ? true : false);
        } catch {
            setError(true);
        } finally {
            setLoadingRecommender(false);
        }
    };

    useEffect(() => {
        if (!!information && !!information?.destination && !information?.departureDate && !information?.returnDate) {
            getRecommenderList();
        }
    }, [information]);

    const [minPriceList, setMinPriceList] = useState();
    useEffect(() => {
        let sumPriceList = [];
        dateRecommenderList?.forEach(recommenderItem => {
            sumPriceList.push(recommenderItem.total_flight_price + recommenderItem.total_hotel_price);
            setMinPriceList(Math.min(...sumPriceList));
        });
    }, [dateRecommenderList]);

    //handle select destination
    const handleSelectedDate = date => {
        domesticFlightInformation.departureDate = date.departure_date;
        domesticFlightInformation.returnDate = date.return_date;
        sessionStorage.setItem("domesticFlightInformation", JSON.stringify(domesticFlightInformation));

        intFlightInformation.departureDate = date.departure_date;
        intFlightInformation.returnDate = date.return_date;
        sessionStorage.setItem("intFlightInformation", JSON.stringify(intFlightInformation));

        hotelInformation.checkIn = date.departure_date;
        hotelInformation.checkOut = date.return_date;
        sessionStorage.setItem("hotelInformation", JSON.stringify(hotelInformation));

        itineraryInformation.departureDate = date.departure_date;
        itineraryInformation.returnDate = date.return_date;
        sessionStorage.setItem("itineraryInformation", JSON.stringify(itineraryInformation));

        setOpen(false);
        if (location.pathname !== `/${locale}/itinerary/hotel`) {
            setRequestFlag(true);
        }
    };

    //format date in Persian format
    const formatDate = date => {
        return formatPersianDate(date, {
            day: "numeric",
            month: "long",
            year: undefined,
            weekday: undefined,
        });
    };

    return (
        <BaseDialog title={`${t("suggestedTimes")} ${t("from")} ${cities}`} open={open} handleClose={handleClose} maxWidth="lg" top="0" icon="back">
            {loadingRecommender && <Loading />}

            <DialogContent dividers={true}>
                <Grid
                    className="without-modal"
                    container
                    item
                    xs={12}
                    rowGap={2}
                    columnGap={{ xs: 1, md: 2 }}
                    justifyContent={{ xs: "space-between", md: "flex-start" }}
                >
                    {error ? (
                        <Grid container item xs={12} justifyContent="center">
                            <EmptyOrErrorState
                                title={t("errorMessage")}
                                icon={<Warning2 size="32" color="var(--red-color)" variant="Bold" />}
                                actionLabel={t("retry")}
                                handleClick={getRecommenderList}
                            />
                        </Grid>
                    ) : dateRecommenderList?.length ? (
                        dateRecommenderList?.map(recommenderItem => {
                            return (
                                <Grid
                                    className={`without-modal__recommender-date ${minPriceList === recommenderItem?.total_price ? "min-price" : ""}`}
                                    container
                                    item
                                    xs={12}
                                    md={2.25}
                                    alignItems="flex-start"
                                    onClick={() => handleSelectedDate(recommenderItem)}
                                >
                                    <Grid
                                        className="without-modal__date-header"
                                        container
                                        item
                                        xs={12}
                                        justifyContent="space-evenly"
                                        alignItems="center"
                                    >
                                        <span className="without-modal__date">
                                            {locale === "en"
                                                ? moment(recommenderItem?.departure_date).format("D MMM")
                                                : formatDate(recommenderItem?.departure_date)}
                                        </span>
                                        <span> {t("to-2")} </span>
                                        <span className="without-modal__date">
                                            {locale === "en"
                                                ? moment(recommenderItem?.return_date).format("D MMM")
                                                : formatDate(recommenderItem?.return_date)}
                                        </span>
                                    </Grid>

                                    <Grid className="without-modal__prices" container item xs={12} rowGap={1}>
                                        <Grid container item xs={12} justifyContent="space-between">
                                            <span>{t("ticketPrice")}:</span>
                                            <span>
                                                {t("from")} {numberWithCommas(recommenderItem?.total_flight_price)} {t("currency")}
                                            </span>
                                        </Grid>
                                        <Grid container item xs={12} justifyContent="space-between">
                                            <span>{t("hotelPrice")}:</span>
                                            <span>
                                                {t("from")} {numberWithCommas(recommenderItem?.total_hotel_price)} {t("currency")}
                                            </span>
                                        </Grid>
                                        <Divider width="100%" />
                                        <Grid container item xs={12} justifyContent="space-between">
                                            <span>{t("total")}:</span>
                                            <span className="without-modal__total-price">
                                                {t("from")} {numberWithCommas(recommenderItem?.total_price)} {t("currency")}
                                            </span>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            );
                        })
                    ) : isEmptyList ? (
                        <Grid container item xs={12} justifyContent="center" alignItems="center">
                            <EmptyOrErrorState
                                title={t("noSuitableTravelTime")}
                                icon={<InfoCircle size="32" color="var(--secondary-color)" variant="Bold" />}
                                actionLabel={t("backToHomePage")}
                                handleClick={handleClose}
                            />
                        </Grid>
                    ) : (
                        <Grid
                            className="without-modal__loading-text"
                            container
                            item
                            xs={12}
                            rowGap={2}
                            direction="column"
                            justifyContent="center"
                            alignItems="center"
                        >
                            <span>{t("lookingForBest")}</span>
                            <span>{t("thisProcessMayTakeTime")}</span>
                        </Grid>
                    )}
                </Grid>
            </DialogContent>
        </BaseDialog>
    );
}

export default WithoutDateModal;
